'use client';

import { useCallback, useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { colors } from '@/styles/theme';
import {
  configApi,
  locationApi,
  loadSystemDict,
  type RobotLocation,
  type RobotFieldOption,
  type RobotStatus,
} from '../../_lib/api';
import { translateApiError } from '../../_lib/translateApiError';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/lib/toast';

const STATUSES: RobotStatus[] = [
  'ORDERED',
  'IN_TRANSIT',
  'BONDED',
  'IN_STOCK',
  'RESERVED',
  'DELIVERED',
  'REPAIR',
  'CANCELLED',
];

interface FfsnRule {
  prefix: string;
  dateFormat: 'YYYYMM' | 'YYYY' | 'YYYYMMDD';
  seqLength: number;
  resetPeriod: 'MONTHLY' | 'YEARLY' | 'NEVER';
}

export default function SettingsAdminPage() {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ffsnRule, setFfsnRule] = useState<FfsnRule>({
    prefix: 'FF',
    dateFormat: 'YYYYMM',
    seqLength: 5,
    resetPeriod: 'MONTHLY',
  });
  const [statusLocationMap, setStatusLocationMap] = useState<Record<string, string | null>>({});
  const [repairServiceType, setRepairServiceType] = useState<string>('REPAIR');

  const [locations, setLocations] = useState<RobotLocation[]>([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState<RobotFieldOption[]>([]);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [ffsnConfig, locMapConfig, repairConfig, locs, svcOpts] = await Promise.all([
        configApi.get('ffsn_rule').catch(() => ({ key: 'ffsn_rule', value: null })),
        configApi.get('status_default_location').catch(() => ({
          key: 'status_default_location',
          value: {},
        })),
        configApi.get('repair_auto_service_type').catch(() => ({
          key: 'repair_auto_service_type',
          value: 'REPAIR',
        })),
        locationApi.list({ enabledOnly: 'true' }),
        loadSystemDict('service_record', 'serviceType'),
      ]);
      if (ffsnConfig.value) setFfsnRule((prev) => ({ ...prev, ...ffsnConfig.value }));
      setStatusLocationMap(locMapConfig.value || {});
      setRepairServiceType(repairConfig.value || 'REPAIR');
      setLocations(locs);
      setServiceTypeOptions(svcOpts);
    } catch (err: any) {
      toast.error(translateApiError(err, t.robotManager) || t.robotManager.settings.configLoadFailed);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const generatePreview = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    let date = '';
    switch (ffsnRule.dateFormat) {
      case 'YYYY':
        date = `${y}`;
        break;
      case 'YYYYMMDD':
        date = `${y}${m}${d}`;
        break;
      default:
        date = `${y}${m}`;
    }
    return `${ffsnRule.prefix}-${date}-${'1'.padStart(ffsnRule.seqLength, '0')}`;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await Promise.all([
        configApi.set('ffsn_rule', ffsnRule, 'FFSN 生成规则'),
        configApi.set(
          'status_default_location',
          statusLocationMap,
          '状态 → 默认 location code 的映射',
        ),
        configApi.set(
          'repair_auto_service_type',
          repairServiceType,
          '进入 REPAIR 状态时自动创建的 ServiceRecord 类型',
        ),
      ]);
      toast.success(t.robotManager.settings.configSaved);
    } catch (err: any) {
      toast.error(translateApiError(err, t.robotManager) || t.robotManager.admin.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  // No sentinel value needed: Select with empty string "" means "infer by UI"
  const INFER_VALUE = '__INFER__';

  return (
    <div className="h-full flex flex-col">
      {/* Title bar */}
      <div
        className="h-12 flex items-center justify-between px-6 shrink-0"
        style={{ backgroundColor: colors.bgPrimary, borderBottom: `1px solid ${colors.border}` }}
      >
        <h1 className="text-base font-semibold" style={{ color: colors.textPrimary }}>
          {t.robotManager.settings.title}
        </h1>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? t.common.saving : t.robotManager.settings.saveAll}
        </Button>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-auto px-6 py-6 max-w-4xl">
        {loading ? (
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-lg shadow p-6" style={{ backgroundColor: colors.bgPrimary }}>
                <Skeleton className="h-6 w-48 mb-4" />
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <p className="text-sm mb-6" style={{ color: colors.textSecondary }}>
              {t.robotManager.settings.description}
            </p>

            {/* FFSN Rule */}
            <div className="rounded-lg shadow mb-6" style={{ backgroundColor: colors.bgPrimary }}>
              <div className="px-6 py-4" style={{ borderBottom: `1px solid ${colors.border}` }}>
                <h2 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>
                  {t.robotManager.settings.ffsn.title}
                </h2>
                <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                  {t.robotManager.settings.ffsn.preview}:{' '}
                  <span className="font-mono" style={{ color: colors.primary }}>
                    {generatePreview()}
                  </span>
                </p>
              </div>
              <div className="px-6 py-4 grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: colors.textPrimary }}
                  >
                    {t.robotManager.settings.ffsn.prefix}
                  </label>
                  <Input
                    value={ffsnRule.prefix}
                    onChange={(e) => setFfsnRule({ ...ffsnRule, prefix: e.target.value })}
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: colors.textPrimary }}
                  >
                    {t.robotManager.settings.ffsn.dateFormat}
                  </label>
                  <Select
                    value={ffsnRule.dateFormat}
                    onValueChange={(value) =>
                      setFfsnRule({ ...ffsnRule, dateFormat: value as FfsnRule['dateFormat'] })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="YYYYMM">YYYYMM</SelectItem>
                      <SelectItem value="YYYY">YYYY</SelectItem>
                      <SelectItem value="YYYYMMDD">YYYYMMDD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: colors.textPrimary }}
                  >
                    {t.robotManager.settings.ffsn.seqLength}
                  </label>
                  <Input
                    type="number"
                    value={ffsnRule.seqLength}
                    onChange={(e) =>
                      setFfsnRule({ ...ffsnRule, seqLength: parseInt(e.target.value) || 5 })
                    }
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: colors.textPrimary }}
                  >
                    {t.robotManager.settings.ffsn.resetPeriod}
                  </label>
                  <Select
                    value={ffsnRule.resetPeriod}
                    onValueChange={(value) =>
                      setFfsnRule({ ...ffsnRule, resetPeriod: value as FfsnRule['resetPeriod'] })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MONTHLY">{t.robotManager.settings.ffsn.monthly}</SelectItem>
                      <SelectItem value="YEARLY">{t.robotManager.settings.ffsn.yearly}</SelectItem>
                      <SelectItem value="NEVER">{t.robotManager.settings.ffsn.never}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Status -> Default Location */}
            <div className="rounded-lg shadow mb-6" style={{ backgroundColor: colors.bgPrimary }}>
              <div className="px-6 py-4" style={{ borderBottom: `1px solid ${colors.border}` }}>
                <h2 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>
                  {t.robotManager.settings.statusLocation.title}
                </h2>
                <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                  {t.robotManager.settings.statusLocation.description}
                </p>
              </div>
              <div className="px-6 py-4">
                <div className="grid grid-cols-2 gap-4">
                  {STATUSES.map((status) => (
                    <div key={status}>
                      <label
                        className="block text-sm font-medium mb-1"
                        style={{ color: colors.textPrimary }}
                      >
                        {t.robotManager.status[status]}
                      </label>
                      <Select
                        value={statusLocationMap[status] ?? INFER_VALUE}
                        onValueChange={(value) =>
                          setStatusLocationMap({
                            ...statusLocationMap,
                            [status]: value === INFER_VALUE ? null : value,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={INFER_VALUE}>
                            {t.robotManager.settings.statusLocation.inferByUI}
                          </SelectItem>
                          {locations.map((l) => (
                            <SelectItem key={l.code} value={l.code}>
                              {l.code} - {l.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* REPAIR Auto ServiceType */}
            <div className="rounded-lg shadow mb-6" style={{ backgroundColor: colors.bgPrimary }}>
              <div className="px-6 py-4" style={{ borderBottom: `1px solid ${colors.border}` }}>
                <h2 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>
                  {t.robotManager.settings.repairServiceType.title}
                </h2>
                <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                  {t.robotManager.settings.repairServiceType.description}
                </p>
              </div>
              <div className="px-6 py-4">
                <div className="max-w-xs">
                  <Select
                    value={repairServiceType}
                    onValueChange={(value) => setRepairServiceType(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypeOptions.map((o) => (
                        <SelectItem key={o.code} value={o.code}>
                          {o.code} - {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
